import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Avatar,
  Box,
  Button,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { PlusCircle, Trash2, Upload } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { v4 as uuid } from "uuid";
import { Territory } from "../../../../../models/territory";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import { FlexContainer } from "../../../../../styles/FlexContainer";
import { useAuth } from "../../../../Reusable/AuthProvider";
import AutocompleteInfiniteScroll from "../../../../Reusable/AutocompleteInfiniteScroll";
import { AutocompleteLabeled } from "../../../../Reusable/AutocompleteLabeled";
import ButtonPrimary from "../../../../Reusable/ButtonPrimary";
import ButtonSecondary from "../../../../Reusable/ButtonSecondary";
import { CardCustom } from "../../../../Reusable/CardCustom";
import SelectLabeled from "../../../../Reusable/SelectLabeled";
import TextFieldLabeled from "../../../../Reusable/TextFieldLabeled";
import ExpertVerificationCard from "./ExpertVerificationCard";

const languages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Chinese",
  "Japanese",
  "Arabic",
  "Russian",
];

export default function EditExpertPage() {
  const editProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    AxiosHttpClient.put("api/v1/docuveritas/users", {
      userId: user.id,
      imageUrl: profileImage,
      title: inputTitle,
      firstName: firstName,
      lastName: lastName,
      phoneNumber: phoneNumber,
      email: email,
      territoryId: inputLocation?.id,
      expertDetails: user.roles.includes("EXPERT")
        ? {
            id: user.expertDetails?.id || uuid(),
            professionalTitle: inputProfessionalTitle,
            description: inputDescription,
            expertiseDomain: inputExpertiseDomain,
            specializations: selectedSpecializations,
            languages: inputLanguages,
            jurisdictionsIds: inputJurisdictions.map((t) => t.id),
            experiences: experiences,
            educations: educations,
            barAssociation: inputBarAssociation,
          }
        : null,
    })
      .then(() => {
        refreshUserInfos();
        navigate("/feature/profile/" + user.id);
      })
      .catch((err) => {});
  };

  const previousController = useRef<AbortController>(null!);

  const { t } = useTranslation();

  const [isLoadingTerritories, setIsLoadingTerritories] = useState(false);
  const [isLoadingJurisdictions, setIsLoadingJurisdictions] = useState(false);

  const getTerritoriesPage = async (pageNumber?: number) => {
    if (previousController.current) {
      previousController.current.abort();
    }
    setIsLoadingTerritories(true);
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;
    try {
      const res = await AxiosHttpClient.get<any>(
        "api/v1/docuveritas/territories/page",
        {
          criteriaList:
            "name=" +
            inputLocationText +
            ",featureClass=P,population=5000=greaterThanEqual",
          pageNumber: (pageNumber ? pageNumber : page) - 1,
          pageSize: 20,
          sort: "name",
          order: "asc",
        },
        signal
      );
      setTotalNumberOfTerritories(Number(res.totalElements));
      (pageNumber ? pageNumber : page) === 1
        ? setTerritories([...res.content])
        : setTerritories([...territories, ...res.content]);
      setPage((pageNumber ? pageNumber : page) === 1 ? 2 : page + 1); // Update page state to next page
    } catch {
      // ...existing code...
    } finally {
      setIsLoadingTerritories(false);
    }
  };

  const previousControllerJurisdictions = useRef<AbortController>(null!);

  const getJurisdictionTerritoriesPage = (pageNumber?: number) => {
    if (previousControllerJurisdictions.current) {
      previousControllerJurisdictions.current.abort();
    }
    setIsLoadingJurisdictions(true);
    const controller = new AbortController();
    const signal = controller.signal;
    previousControllerJurisdictions.current = controller;
    AxiosHttpClient.get<any>(
      "api/v1/docuveritas/territories/page",
      {
        criteriaList: "id=2542007",
        pageNumber: (pageNumber ? pageNumber : pageJurisdictions) - 1,
        pageSize: 20,
        sort: "name",
        order: "asc",
      },
      signal
    )
      .then((res) => {
        setTotalNumberOfJurisdictionsTerritories(Number(res.totalElements));
        (pageNumber ? pageNumber : pageJurisdictions) === 1
          ? setJurisdictionsTerritories([...res.content])
          : setJurisdictionsTerritories([...territories, ...res.content]);
      })
      .catch(() => {})
      .finally(() => setIsLoadingJurisdictions(false));
  };

  let fetchMoreData: any = () => {
    if (page > totalNumberOfTerritories / 20) {
      getTerritoriesPage(page);
      setHasMore(false);
      return;
    }
    getTerritoriesPage(page);
  };

  let fetchMoreJurisdictionsData: any = () => {
    if (page > totalNumberOfTerritories / 20) {
      setHasMoreJurisdictions(false);
      return;
    }
    setPageJurisdictions(page + 1);
  };

  const [page, setPage] = useState(1);

  const [pageJurisdictions, setPageJurisdictions] = useState(1);

  const [totalNumberOfTerritories, setTotalNumberOfTerritories] = useState(0);

  const [
    totalNumberOfJurisdictionsTerritories,
    setTotalNumberOfJurisdictionsTerritories,
  ] = useState(0);

  const [hasMore, setHasMore] = useState(true);

  const [hasMoreJurisdictions, setHasMoreJurisdictions] = useState(true);

  const [territories, setTerritories] = useState<Territory[]>([]);

  const [jurisdictionsTerritories, setJurisdictionsTerritories] = useState<
    Territory[]
  >([]);

  const [inputLocation, setInputLocation] = useState<Territory | null>(null);

  const [inputLocationText, setInputLocationText] = useState("");

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [inputTitle, setInputTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [inputProfessionalTitle, setInputProfessionalTitle] = useState("");
  const [inputExpertiseDomain, setInputExpertiseDomain] = useState("");
  const [selectedSpecializations, setSelectedSpecializations] = useState<
    string[]
  >([]);
  const [inputDescription, setInputDescription] = useState("");
  const [inputLanguages, setInputLanguages] = useState<string[]>([]);
  const [inputJurisdictions, setInputJurisdictions] = useState<Territory[]>([]);
  const [experiences, setExperiences] = useState([
    {
      id: uuid(),
      jobTitle: "",
      companyName: "",
      startDate: null as Dayjs | null,
      endDate: null as Dayjs | null,
      current: false,
    },
  ]);
  const [educations, setEducations] = useState([
    {
      id: uuid(),
      degreeName: "",
      institution: "",
      endYear: undefined as number | undefined,
    },
  ]);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");

  const [inputJurisdictionsText, setInputJurisdictionsText] = useState("");
  const [inputBarAssociation, setInputBarAssociation] = useState("");

  const { user, refreshUserInfos } = useAuth();

  const navigate = useNavigate();

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        ev.target?.result && setProfileImage(ev.target.result as string);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const addExperience = () =>
    setExperiences([
      ...experiences,
      {
        id: uuid(),
        jobTitle: "",
        companyName: "",
        startDate: null,
        endDate: null,
        current: false,
      },
    ]);
  const removeExperience = (i: number) =>
    setExperiences(experiences.filter((_, idx) => idx !== i));
  const updateExperience = (i: number, field: string, value: any) => {
    const copy = [...experiences];
    // @ts-ignore
    copy[i][field] = value;
    setExperiences(copy);
  };

  const addEducation = () =>
    setEducations([
      ...educations,
      {
        id: uuid(),
        degreeName: "",
        institution: "",
        endYear: undefined,
      },
    ]);
  const removeEducation = (i: number) =>
    setEducations(educations.filter((_, idx) => idx !== i));
  const updateEducation = (i: number, field: string, value: any) => {
    const copy = [...educations];
    // @ts-ignore
    copy[i][field] = value;
    setEducations(copy);
  };

  const toggleArray = (arr: string[], set: (v: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const years = Array.from({ length: 50 }, (_, i) => dayjs().year() - i);

  useEffect(() => {
    setHasMore(true);
    getTerritoriesPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputLocationText]);

  useEffect(() => {
    if (pageJurisdictions !== 1) getJurisdictionTerritoriesPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageJurisdictions]);

  useEffect(() => {
    getJurisdictionTerritoriesPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputJurisdictionsText]);

  useEffect(() => {
    if (user) {
      setProfileImage(user.imageUrl || null);
      setInputTitle(user.title || "");
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setPhoneNumber(user.phoneNumber || "");
      setEmail(user.email || "");
      setInputProfessionalTitle(user.expertDetails?.professionalTitle || "");
      setInputDescription(user.expertDetails?.description || "");
      setInputExpertiseDomain(user.expertDetails?.expertiseDomain || "");
      setSelectedSpecializations(user.expertDetails?.specialties || []);
      setInputLanguages(user.expertDetails?.languages || []);
      setInputJurisdictions(user.expertDetails?.jurisdictions || []);
      setExperiences(user.expertDetails?.experiences || []);
      setEducations(user.expertDetails?.educations || []);
      setInputBarAssociation(user.expertDetails?.barAssociation || "");
      setInputLocation(user.location || null);
    }
  }, [user]);

  // Use stable keys for specializations
  const specializationKeys = [
    "CORPORATE_LAW",
    "CRIMINAL_LAW",
    "FAMILY_LAW",
    "INTELLECTUAL_PROPERTY",
    "TAX_LAW",
    "ENVIRONMENTAL_LAW",
    "LABOR_LAW",
    "REAL_ESTATE_LAW",
    "IMMIGRATION_LAW",
    "CONSTITUTIONAL_LAW",
  ];

  const getSpecializationLabel = (specialization: string) => {
    const translationKey = `EDIT_EXPERT_PAGE_SPECIALIZATION_${specialization}`;
    const translated = t(translationKey);
    return translated === translationKey ? specialization : translated;
  };

  const expertiseDomainKeys = [
    "CERTIFIED_PUBLIC_ACCOUNTANT",
    "CHARTERED_ACCOUNTANT",
    "LAWYER",
    "NOTARY",
  ];

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        width: "100%",
        py: 6,
        bgcolor: "hsl(var(--background))",
        px: { xs: 4, md: 6 },
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={4}
        sx={{ color: "hsl(var(--foreground))" }}
      >
        {t("EDIT_EXPERT_PAGE_TITLE")}
      </Typography>

      <Box
        component="form"
        onSubmit={editProfile}
        sx={{ display: "grid", gap: 8 }}
      >
        {/* General Information */}
        <CardCustom>
          <CardHeader
            title={t("EDIT_EXPERT_PAGE_SECTION_GENERAL")}
            subheader={t("EDIT_EXPERT_PAGE_SECTION_GENERAL_SUB")}
            sx={{
              "& .MuiCardHeader-title": {
                color: "hsl(var(--foreground))",
                fontWeight: 600,
              },
              "& .MuiCardHeader-subheader": {
                color: "hsl(var(--muted-foreground))",
              },
            }}
          />
          <CardContent
            sx={{
              display: "grid",
              gap: 6,
              bgcolor: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ position: "relative", width: 128, height: 128 }}>
                {profileImage ? (
                  <Avatar
                    src={profileImage}
                    alt="Profile"
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: 4,
                      borderColor: "hsl(var(--border))",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: "hsl(var(--muted))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                    }}
                  >
                    <Upload
                      style={{
                        height: "40px",
                        width: "40px",
                        color: "hsl(var(--muted-foreground))",
                      }}
                    />
                  </Box>
                )}
                <IconButton
                  component="label"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "hsl(var(--primary))",
                    color: "hsl(var(--primary-foreground))",
                    "&:hover": { bgcolor: "hsl(var(--primary) / 0.9)" },
                  }}
                >
                  <Upload
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                  />
                </IconButton>
              </Box>
              <Typography
                fontSize="14px"
                sx={{ color: "hsl(var(--muted-foreground))" }}
              >
                {t("EDIT_EXPERT_PAGE_UPLOAD_PHOTO")}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SelectLabeled
                  label={t("EDIT_EXPERT_PAGE_LABEL_TITLE")}
                  value={inputTitle}
                  onChange={(e) => {
                    setInputTitle(e.target.value);
                  }}
                  options={[
                    { value: "Mr.", label: "Mr." },
                    { value: "Mme", label: "Mme" },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AutocompleteInfiniteScroll<Territory>
                  // pagination et recherche gérées par le composant
                  loadOptions={fetchMoreData}
                  clearOnBlur={false} // optionnel
                  initialOptions={territories} // facultatif : vos 1eres données
                  hasMore={hasMore} // optionnel override
                  isLoading={isLoadingTerritories} // dynamique
                  // props Autocomplete contrôlés
                  isOptionEqualToValue={(t, v) => t.id === v.id}
                  id="location-autocomplete"
                  label={t("EDIT_EXPERT_PAGE_LABEL_LOCATION")}
                  value={inputLocation}
                  onChange={(_, v) => setInputLocation(v)}
                  inputValue={inputLocationText}
                  onInputChange={(e, v) => setInputLocationText(v)}
                  getOptionLabel={(t) =>
                    `${t.name}, ${t.parents[0]?.parents[0]?.name}`
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldLabeled
                  fullWidth
                  label={t("EDIT_EXPERT_PAGE_LABEL_FIRST_NAME")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldLabeled
                  fullWidth
                  label={t("EDIT_EXPERT_PAGE_LABEL_LAST_NAME")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldLabeled
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t("EDIT_EXPERT_PAGE_LABEL_EMAIL")}
                      <Tooltip
                        title={t("EDIT_EXPERT_PAGE_EMAIL_DISCLAIMER")}
                        arrow
                        placement="right"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: "13px",
                              maxWidth: 260,
                              bgcolor: "hsl(var(--card))",
                              color: "hsl(var(--foreground))",
                              boxShadow: 2,
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "hsl(var(--muted-foreground))",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>
                  }
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldLabeled
                  fullWidth
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t("EDIT_EXPERT_PAGE_LABEL_PHONE")}
                      <Tooltip
                        title={t("EDIT_EXPERT_PAGE_PHONE_DISCLAIMER")}
                        arrow
                        placement="right"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: "13px",
                              maxWidth: 260,
                              bgcolor: "hsl(var(--card))",
                              color: "hsl(var(--foreground))",
                              boxShadow: 2,
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "hsl(var(--muted-foreground))",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>
                  }
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Grid>
              {user?.roles.includes("USER") && (
                <Grid item xs={12}>
                  <AutocompleteLabeled
                    label={t("EDIT_EXPERT_PAGE_LABEL_LANGUAGES")}
                    multiple
                    disableCloseOnSelect
                    options={languages}
                    value={inputLanguages}
                    onChange={(_, value) => setInputLanguages(value)}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </CardCustom>
        {user?.roles.includes("EXPERT") && (
          <>
            {/* About Me */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_ABOUT")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_ABOUT_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <TextFieldLabeled
                  fullWidth
                  multiline
                  minRows={4}
                  label={t("EDIT_EXPERT_PAGE_LABEL_DESCRIPTION")}
                  value={inputDescription}
                  onChange={(e) => setInputDescription(e.target.value)}
                />
              </CardContent>
            </CardCustom>

            {/* Legal Specializations */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_SPECIALIZATIONS")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_SPECIALIZATIONS_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <SelectLabeled
                  label={t("EDIT_EXPERT_PAGE_LABEL_EXPERTISE_DOMAIN")}
                  value={inputExpertiseDomain}
                  onChange={(e) => setInputExpertiseDomain(e.target.value)}
                  options={[
                    {
                      value: "",
                      label: t(
                        "EDIT_EXPERT_PAGE_OPTION_SELECT_EXPERTISE_DOMAIN"
                      ),
                    },
                    ...expertiseDomainKeys.map((option) => ({
                      value: option,
                      label: t(
                        `EDIT_EXPERT_PAGE_EXPERTISE_DOMAIN_${option}`
                      ),
                    })),
                  ]}
                />
                <AutocompleteLabeled
                  label={t("EDIT_EXPERT_PAGE_LABEL_SPECIALIZATIONS")}
                  multiple
                  freeSolo
                  options={specializationKeys}
                  value={selectedSpecializations}
                  onChange={(_, value) => setSelectedSpecializations(value)}
                  getOptionLabel={(option) => getSpecializationLabel(option)}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      const { key, ...other } = tagProps;
                      return (
                        <Chip
                          key={key}
                          {...other}
                          label={getSpecializationLabel(option)}
                          sx={{
                            bgcolor: "hsl(var(--muted))",
                            color: "hsl(var(--foreground))",
                            "& .MuiChip-deleteIcon": {
                              color: "hsl(var(--muted-foreground))",
                              "&:hover": {
                                color: "hsl(var(--destructive))",
                              },
                            },
                          }}
                        />
                      );
                    })
                  }
                />
              </CardContent>
            </CardCustom>

            {/* Experience */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_EXPERIENCE")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_EXPERIENCE_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <Stack spacing={2}>
                  {experiences.map((exp, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "hsl(var(--border))",
                        borderRadius: 1,
                        position: "relative",
                      }}
                    >
                      {experiences.length > 1 && (
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "hsl(var(--muted-foreground))",
                            "&:hover": {
                              bgcolor: "hsl(var(--muted))",
                              color: "hsl(var(--destructive))",
                            },
                          }}
                          onClick={() => removeExperience(i)}
                        >
                          <Trash2 fontSize="small" />
                        </IconButton>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextFieldLabeled
                            fullWidth
                            label={t("EDIT_EXPERT_PAGE_LABEL_JOB_TITLE")}
                            value={exp.jobTitle}
                            onChange={(e) =>
                              updateExperience(i, "jobTitle", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextFieldLabeled
                            fullWidth
                            label={t("EDIT_EXPERT_PAGE_LABEL_COMPANY_NAME")}
                            value={exp.companyName}
                            onChange={(e) =>
                              updateExperience(i, "companyName", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              label={t("EDIT_EXPERT_PAGE_LABEL_START_DATE")}
                              inputFormat="DD/MM/YYYY"
                              value={exp.startDate}
                              onChange={(newValue) =>
                                updateExperience(i, "startDate", newValue)
                              }
                              renderInput={(params) => (
                                <TextFieldLabeled
                                  label={t("EDIT_EXPERT_PAGE_LABEL_START_DATE")}
                                  placeholder={t(
                                    "EDIT_EXPERT_PAGE_PLACEHOLDER_CHOOSE_DATE"
                                  )}
                                  value={params.inputProps?.value || ""}
                                  onChange={params.inputProps?.onChange as any}
                                  {...params}
                                  sx={{
                                    "& .MuiIconButton-root": {
                                      color: "hsl(var(--muted-foreground))",
                                      "&:hover": {
                                        bgcolor: "hsl(var(--muted))",
                                        color: "hsl(var(--foreground))",
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              label={
                                <FlexContainer justifyContent={"space-between"}>
                                  <div>
                                    {t("EDIT_EXPERT_PAGE_LABEL_END_DATE")}
                                  </div>
                                  <FlexContainer alignItems={"center"} gap={1}>
                                    <Checkbox
                                      checked={exp.current}
                                      onChange={(e) =>
                                        updateExperience(
                                          i,
                                          "current",
                                          e.target.checked
                                        )
                                      }
                                      sx={{
                                        height: 17,
                                        width: 17,
                                        padding: 0,
                                        color: "hsl(var(--muted-foreground))",
                                        "&.Mui-checked": {
                                          color: "hsl(var(--primary))",
                                        },
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "hsl(var(--foreground))" }}
                                    >
                                      {t("EDIT_EXPERT_PAGE_LABEL_PRESENT")}
                                    </Typography>
                                  </FlexContainer>
                                </FlexContainer>
                              }
                              disabled={exp.current}
                              inputFormat="DD/MM/YYYY"
                              value={exp.endDate}
                              onChange={(newValue) =>
                                updateExperience(i, "endDate", newValue)
                              }
                              renderInput={(params) => (
                                <TextFieldLabeled
                                  label={""}
                                  placeholder={t(
                                    "EDIT_EXPERT_PAGE_PLACEHOLDER_CHOOSE_DATE"
                                  )}
                                  value={params.inputProps?.value || ""}
                                  onChange={params.inputProps?.onChange as any}
                                  {...params}
                                  sx={{
                                    "& .MuiIconButton-root": {
                                      color: "hsl(var(--muted-foreground))",
                                      "&:hover": {
                                        bgcolor: "hsl(var(--muted))",
                                        color: "hsl(var(--foreground))",
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<PlusCircle />}
                    onClick={addExperience}
                    sx={{
                      borderColor: "hsl(var(--border))",
                      color: "hsl(var(--foreground))",
                      "&:hover": {
                        bgcolor: "hsl(var(--muted))",
                        borderColor: "hsl(var(--border))",
                      },
                    }}
                  >
                    {t("EDIT_EXPERT_PAGE_BUTTON_ADD_EXPERIENCE")}
                  </Button>
                </Stack>
              </CardContent>
            </CardCustom>

            {/* Education */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_EDUCATION")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_EDUCATION_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <Stack spacing={2}>
                  {educations.map((ed, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "hsl(var(--border))",
                        borderRadius: 1,
                        position: "relative",
                      }}
                    >
                      {educations.length > 1 && (
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "hsl(var(--muted-foreground))",
                            "&:hover": {
                              bgcolor: "hsl(var(--muted))",
                              color: "hsl(var(--destructive))",
                            },
                          }}
                          onClick={() => removeEducation(i)}
                        >
                          <Trash2 fontSize="small" />
                        </IconButton>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextFieldLabeled
                            fullWidth
                            label={t("EDIT_EXPERT_PAGE_LABEL_DEGREE_NAME")}
                            value={ed.degreeName}
                            onChange={(e) =>
                              updateEducation(i, "degreeName", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextFieldLabeled
                            fullWidth
                            label={t("EDIT_EXPERT_PAGE_LABEL_INSTITUTION")}
                            value={ed.institution}
                            onChange={(e) =>
                              updateEducation(i, "institution", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <SelectLabeled
                            label={t("EDIT_EXPERT_PAGE_LABEL_END_YEAR")}
                            value={ed.endYear?.toString() || ""}
                            onChange={(e) =>
                              updateEducation(
                                i,
                                "endYear",
                                Number(e.target.value)
                              )
                            }
                            options={years.map((year) => ({
                              value: year.toFixed(0),
                              label: year.toFixed(0),
                            }))}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<PlusCircle />}
                    onClick={addEducation}
                    sx={{
                      borderColor: "hsl(var(--border))",
                      color: "hsl(var(--foreground))",
                      "&:hover": {
                        bgcolor: "hsl(var(--muted))",
                        borderColor: "hsl(var(--border))",
                      },
                    }}
                  >
                    {t("EDIT_EXPERT_PAGE_BUTTON_ADD_EDUCATION")}
                  </Button>
                </Stack>
              </CardContent>
            </CardCustom>

            {/* Credentials */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_CREDENTIALS")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_CREDENTIALS_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextFieldLabeled
                      fullWidth
                      label={t("EDIT_EXPERT_PAGE_LABEL_BAR_ASSOC")}
                      value={inputBarAssociation}
                      onChange={(e) => setInputBarAssociation(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </CardCustom>

            {/* Languages & Jurisdictions */}
            <CardCustom>
              <CardHeader
                title={t("EDIT_EXPERT_PAGE_SECTION_LANGUAGES_JURIS")}
                subheader={t("EDIT_EXPERT_PAGE_SECTION_LANGUAGES_JURIS_SUB")}
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "hsl(var(--foreground))",
                    fontWeight: 600,
                  },
                  "& .MuiCardHeader-subheader": {
                    color: "hsl(var(--muted-foreground))",
                  },
                }}
              />
              <CardContent
                sx={{
                  bgcolor: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AutocompleteLabeled
                      label={t("EDIT_EXPERT_PAGE_LABEL_LANGUAGES")}
                      multiple
                      disableCloseOnSelect
                      options={languages}
                      value={inputLanguages}
                      onChange={(_, value) => setInputLanguages(value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AutocompleteInfiniteScroll<Territory>
                      id="jurisdictions-autocomplete"
                      label={t("EDIT_EXPERT_PAGE_LABEL_JURISDICTIONS")}
                      /* multiple only (freeSolo is false by default) */
                      multiple
                      isOptionEqualToValue={(t, v) => t.id === v.id}
                      /* controlled selected values (Territory[]) */
                      value={inputJurisdictions}
                      onChange={(_, val) =>
                        setInputJurisdictions(val as Territory[])
                      }
                      /* controlled text in the input */
                      inputValue={inputJurisdictionsText}
                      onInputChange={(_, val) => setInputJurisdictionsText(val)}
                      /* your data and paging callbacks */
                      initialOptions={jurisdictionsTerritories}
                      loadOptions={fetchMoreJurisdictionsData}
                      hasMore={hasMoreJurisdictions}
                      isLoading={isLoadingJurisdictions} // dynamique
                      /* how to display each Territory */
                      getOptionLabel={(t) => `${t.name}, ${t.countryCode}`}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </CardCustom>

            {/* Vérification du profil expert */}
            <ExpertVerificationCard />
          </>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonSecondary
            onClick={() => {
              navigate("/feature/profile/" + user?.id);
            }}
          >
            {t("EDIT_EXPERT_PAGE_BUTTON_CANCEL")}
          </ButtonSecondary>
          <ButtonPrimary type="submit">
            {t("EDIT_EXPERT_PAGE_BUTTON_SAVE")}
          </ButtonPrimary>
        </Box>
      </Box>
    </Box>
  );
}
